import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import db from '../lib/db'
import jsQR from 'jsqr'
import { routeQR } from '../lib/qrRouter'
import { checkFragoObjectives, activateFragosByEntityScan, findPlayerScanObjective, onKillRecorded, activateFragoByQrCode, checkEscortObjectives, checkFragoFailConditions } from '../lib/fragoEngine'

export default function QRScanner({ user, onResult, autoStart = false, simulatedQrText = null }) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const intervalRef = useRef(null)
  const [scanning, setScanning] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [joinProtectBusy, setJoinProtectBusy] = useState(false)

  useEffect(() => { return () => stopScanner() }, [])
  useEffect(() => {
    // Dev tools can pass a synthetic QR text to bypass the camera. When
    // present, run the normal handler against that string and skip the
    // camera permission + jsQR loop entirely.
    if (simulatedQrText) {
      handleClaim(simulatedQrText)
      return
    }
    if (autoStart) startScanner()
  }, [autoStart, simulatedQrText])
  // Auto-redirect to player view after successful scan. Intel and equipment
  // scans surface a longer narrative payload (intel message, post-scan
  // instruction) that the player needs time to read — those stay open
  // until the player taps DONE. Kill / checkpoint scans auto-close after
  // 60s (TD-18) so the result still clears on its own if the operator
  // never gets back to the phone, but a player who's still on-screen has
  // time to actually read the message + decide on a follow-up action.
  useEffect(() => {
    if (result?.success && onResult) {
      const isReadable = result.intel || result.equipment
      if (isReadable) return
      const timer = setTimeout(() => {
        onResult()
      }, 60000)
      return () => clearTimeout(timer)
    }
  }, [result])


  const useNativeDetector = () => 'BarcodeDetector' in window

  const startScanner = async () => {
    setError(''); setResult(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      streamRef.current = stream
      videoRef.current.srcObject = stream
      await videoRef.current.play()
      setScanning(true)

      if (useNativeDetector()) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        intervalRef.current = setInterval(async () => {
          try {
            const codes = await detector.detect(videoRef.current)
            if (codes.length > 0) { clearInterval(intervalRef.current); stopStream(); setScanning(false); await handleClaim(codes[0].rawValue) }
          } catch {}
        }, 500)
      } else {
        const canvas = canvasRef.current; const ctx = canvas.getContext('2d')
        intervalRef.current = setInterval(() => {
          const video = videoRef.current
          if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return
          canvas.width = video.videoWidth; canvas.height = video.videoHeight
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const code = jsQR(imageData.data, imageData.width, imageData.height)
          if (code) { clearInterval(intervalRef.current); stopStream(); setScanning(false); handleClaim(code.data) }
        }, 500)
      }
    } catch (err) { setError('Camera access denied. Please allow camera permissions.') }
  }

  const stopStream = () => { clearInterval(intervalRef.current); if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()) }
  const stopScanner = () => { stopStream(); setScanning(false) }

  // Detect friendly-fire kill type — fully event-scoped.
  //
  // Returns:
  //   null            normal kill (different factions / no shared team)
  //   'team_kill'     killer + target share event_players.team_id
  //                   (the per-event roster team — overrides faction)
  //   'faction_kill'  same faction, different (or no) team
  //
  // Bug 45 — the previous implementation walked team_members across all
  // events the user has ever been on, which made the check unreliable
  // (a player on Team Alpha at event X and Team Bravo at event Y would
  // count as "shared team" with someone who happened to be on Team Alpha
  // at event Y). event_players.team_id is the authoritative per-event
  // team and is what the roster + MCS surfaces use, so we pivot on that.
  const detectFriendlyFire = async (scannerId, targetId, eventId) => {
    if (!eventId) return null

    const { data: rows } = await supabase
      .from('event_players')
      .select('user_id, faction_id, team_id')
      .eq('event_id', eventId)
      .in('user_id', [scannerId, targetId])

    const scanner = rows?.find(p => p.user_id === scannerId)
    const target  = rows?.find(p => p.user_id === targetId)
    if (!scanner || !target) return null

    // Same-team check wins even if factions are misaligned (theoretical:
    // a GM-mis-assignment that crossed factions still records as a team
    // kill because the human-level "you killed your buddy" signal is
    // what matters).
    if (scanner.team_id && target.team_id && scanner.team_id === target.team_id) {
      return 'team_kill'
    }

    // Same faction, different / no team → faction_kill.
    if (scanner.faction_id && target.faction_id && scanner.faction_id === target.faction_id) {
      return 'faction_kill'
    }

    return null
  }

  // ANU-103 — fail active PROTECT bounties when VIP is killed by enemy
  const failProtectBounties = async (targetUserId, killerUserId) => {
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) return
    const { data: activeProtects } = await supabase
      .from('bounties')
      .select('id')
      .eq('vip_player_id', targetUserId)
      .eq('event_id', storedEventId)
      .eq('bounty_type', 'protect')
      .eq('status', 'active')
    if (activeProtects?.length > 0) {
      for (const pb of activeProtects) {
        await supabase.from('bounties').update({ status: 'failed' }).eq('id', pb.id)
        await supabase.from('mission_events').insert({
          bounty_id: pb.id,
          event_type: 'vip_down',
          triggered_by: killerUserId,
          notes: 'VIP eliminated by enemy — protect bounty failed',
        })
      }
    }
  }

  const failDefendBounties = async (targetUserId, triggeredByUserId) => {
    const { data: activeDefends } = await supabase
      .from('bounties')
      .select('id, linked_bounty_id')
      .eq('target_id', targetUserId)
      .eq('bounty_type', 'defend')
      .eq('status', 'active')
      .in('phase', ['prep', 'active'])

    if (activeDefends?.length > 0) {
      for (const defend of activeDefends) {
        await supabase.from('bounties')
          .update({ status: 'failed', phase: 'failed' })
          .eq('id', defend.id)

        await supabase.from('mission_events').insert({
          bounty_id: defend.id,
          event_type: 'vip_down',
          triggered_by: triggeredByUserId,
          notes: 'VIP killed — defend bounty failed',
        })

        await supabase.from('bounty_guards')
          .update({ status: 'unverified' })
          .eq('bounty_id', defend.id)
          .in('status', ['checked_in', 'active'])
      }
    }
    return activeDefends?.length || 0
  }

  // Phase 6 of the FRAGO Rework — ANUBIS-FRAGO-{id} handler. Field QR
  // dropped to activate a structured FRAGO. activateFragoByQrCode looks
  // up the frago_trigger_qr row, marks scanned, and flips the FRAGO
  // queued→active. Surface friendly errors for unknown / already-scanned
  // / cross-event codes rather than failing silently.
  const handleFragoTriggerScan = async (rawQrCode) => {
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) {
      setResult({ success: false, message: 'Join an event first. Scan an ANUBIS-JOIN code or pick one in EVENTS.' })
      return
    }
    const result = await activateFragoByQrCode(rawQrCode, user.id, storedEventId)
    if (!result.ok) {
      const msg = result.reason === 'unknown_qr'      ? 'This FRAGO QR is not registered.'
                : result.reason === 'wrong_event'     ? 'This FRAGO belongs to a different event.'
                : result.reason === 'already_scanned' ? 'This FRAGO has already been activated.'
                : 'FRAGO activation failed.'
      setResult({ success: false, message: msg })
      return
    }
    setResult({
      success: true,
      equipment: true, // re-use the readable-result panel that stays open until DONE
      message: '◆ FRAGO ACTIVATED',
      intelMessage: result.missionName
        ? `Your scan triggered: ${result.missionName}. Mission is now active — check MISSIONS tab for orders.`
        : 'Mission unlocked — check MISSIONS tab for orders.',
      postScanInstruction: 'LEAVE QR IN PLACE',
    })
  }

  // Intel-trigger scan handler.
  //
  // Reads the trigger row, gates on (a) event match, (b) faction-already-
  // scanned (UNIQUE on intel_trigger_scans takes care of this), (c) global
  // already-scanned when allow_faction_rescan = false. Then writes the scan
  // row, flips trigger.status hidden→found on first scan, moves the linked
  // bounty queued→awaiting_delegation, awards faction + scanner points, and
  // logs to mission_events.
  //
  // Phase 6 of the FRAGO Rework deprecates this path — new-schema FRAGO
  // activation rides ANUBIS-FRAGO-{id} via handleFragoTriggerScan. This
  // handler stays alive so events created before the rework keep working.
  const handleIntelScan = async (triggerId) => {
    if (!triggerId || triggerId.length < 8) {
      setResult({ success: false, message: 'Invalid intel QR' })
      return
    }
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) {
      setResult({ success: false, message: 'Join an event first. Scan an ANUBIS-JOIN code or pick one in EVENTS.' })
      return
    }

    // Player's faction + node in this event (used for scope + context)
    const { data: epRows } = await supabase.from('event_players')
      .select('faction_id').eq('user_id', user.id).eq('event_id', storedEventId).limit(1)
    const factionId = epRows?.[0]?.faction_id || null
    if (!factionId) {
      setResult({ success: false, message: 'You have no faction assigned for this event.' })
      return
    }
    const { data: memRows } = await supabase.from('mcs_node_members')
      .select('node_id, mcs_nodes!inner(event_id)').eq('user_id', user.id)
    const nodeId = (memRows || []).find(r => r.mcs_nodes?.event_id === storedEventId)?.node_id || null

    // Trigger row
    const { data: trgRows } = await supabase.from('intel_triggers')
      .select('*').eq('id', triggerId).limit(1)
    const trigger = trgRows?.[0]
    if (!trigger) {
      setResult({ success: false, message: 'Intel trigger not found.' })
      return
    }
    if (trigger.event_id !== storedEventId) {
      setResult({ success: false, message: 'This intel belongs to a different event.' })
      return
    }
    // Entity-attached triggers (player / equipment / checkpoint) are
    // discovered by scanning the entity, not by hand-scanning the intel QR
    // directly. Reject so a tester scanning a planted-on-equipment intel
    // QR can't bypass the discovery flow.
    if (trigger.target_type && trigger.target_type !== 'field') {
      setResult({ success: false, message: 'This intel must be discovered through its target, not by direct scan.' })
      return
    }

    // Existing scans
    const { data: scans } = await supabase.from('intel_trigger_scans')
      .select('faction_id').eq('trigger_id', triggerId)
    const scansList = scans || []
    if (scansList.some(s => s.faction_id === factionId)) {
      setResult({ success: false, message: '⚠ INTEL ALREADY ACQUIRED BY YOUR FACTION' })
      return
    }
    if (!trigger.allow_faction_rescan && scansList.length > 0) {
      setResult({ success: false, message: '⚠ INTEL ALREADY ACQUIRED' })
      return
    }

    // The intel_trigger_scans INSERT fires resolve_intel_scan() (SECURITY
    // DEFINER) which atomically flips intel_triggers.status, advances any
    // linked bounty to awaiting_delegation, logs the mission_events trail,
    // and awards both scanner + faction points. Everything below this line
    // used to be client-side follow-up writes — most were silently blocked
    // by RLS for regular players. Server-side trigger handles it now.
    const { error: scanErr } = await supabase.from('intel_trigger_scans').insert({
      trigger_id: triggerId,
      faction_id: factionId,
      scanned_by: user.id,
      node_id: nodeId,
    })
    if (scanErr) {
      setResult({ success: false, message: 'Scan failed: ' + scanErr.message })
      return
    }

    setResult({
      success: true,
      intel: true,
      message: '◆ INTEL ACQUIRED',
      intelMessage: trigger.message || null,
      postScanInstruction: trigger.post_scan_instruction || 'LEAVE IN PLACE',
      points: (trigger.player_intel_points || 0),
    })
  }

  // Checkpoint QR handler. Delegates the entire state machine to the
  // scan_checkpoint() RPC and renders the returned message + countdown.
  // Offline path queues the scan in the outbox for replay on reconnect.
  const handleCheckpointScan = async (cpId) => {
    if (!cpId || cpId.length < 8) {
      setResult({ success: false, message: 'Invalid checkpoint QR' })
      return
    }
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) {
      setResult({ success: false, message: 'Join an event first. Scan an ANUBIS-JOIN code or pick one in EVENTS.' })
      return
    }

    if (!navigator.onLine) {
      try {
        await db.queueCheckpointScan({ checkpointId: cpId, userId: user.id, eventId: storedEventId })
        setResult({
          success: true,
          checkpoint: true,
          message: '◆ CHECKPOINT QUEUED',
          intelMessage: 'Saved offline — will sync when you reconnect.',
          postScanInstruction: 'No timer feedback while offline.',
        })
      } catch (err) {
        setResult({ success: false, message: 'Offline queue failed: ' + (err?.message || 'unknown') })
      }
      return
    }

    // FIX 4 — FRAGO-objective bypass. Before hitting scan_checkpoint
    // (which RAISEs when the org has cp_enabled=false), see if this
    // CP is a live FRAGO objective for the scanner. If yes, process
    // as a FRAGO completion / activation and skip the capture state
    // machine entirely — the org gate is about the standalone
    // capture feature, not mission completion.
    {
      const fragoObjective = await checkFragoObjectives('checkpoint', cpId, storedEventId, user.id)
      const fragoEscort    = await checkEscortObjectives(cpId, storedEventId, user.id)
      const { activated: fragoActivated } =
        await activateFragosByEntityScan('checkpoint_scan', cpId, storedEventId, user.id)
      if (fragoObjective.completed || fragoEscort.completed || fragoActivated.length > 0) {
        const parts = []
        if (fragoObjective.completed) parts.push(`◆ ${fragoObjective.matchedTitle}`)
        if (fragoEscort.completed)    parts.push(`◆ ${fragoEscort.matchedTitle} (escort delivered)`)
        if (fragoObjective.phaseAdvanced) parts.push('Phase advanced.')
        if (fragoObjective.fragoCompleted) parts.push('MISSION SUCCESS.')
        if (fragoActivated.length > 0) parts.push(`FRAGO triggered: ${fragoActivated[0].mission_name}`)
        const triggered = fragoActivated[0]
        setResult({
          success: true,
          checkpoint: true,
          message: triggered
            ? `▲ INTEL DISCOVERED — ${triggered.mission_name || 'FRAGO'}`
            : '◆ OBJECTIVE COMPLETE',
          intelMessage: triggered?.activation_message || parts.join(' ') || null,
          postScanInstruction: parts.length > 0 ? parts.join(' ') : null,
        })
        return
      }
    }

    const { data, error } = await supabase.rpc('scan_checkpoint', { p_checkpoint_id: cpId })
    if (error) {
      const msg = error.message || ''
      const noFaction = msg.toLowerCase().includes('no faction') || msg.toLowerCase().includes('not on the event roster')
      setResult({
        success: false,
        message: noFaction ? 'No faction assigned for this event — contact your GM.' : (msg || 'Checkpoint scan failed'),
      })
      return
    }
    const row = Array.isArray(data) ? data[0] : data
    const status = row?.status
    const remaining = row?.timer_remaining_seconds
    const startedAt = row?.capture_started_at
    // Compute live countdown from the engine-side timestamp so the
    // scanner display reflects "time left" not "duration set" — handy
    // when reinforcement reduced the remaining window.
    let countdown = null
    if (status === 'capturing' && startedAt && remaining != null) {
      const started = new Date(startedAt).getTime()
      const elapsed = Math.max(0, Math.floor((Date.now() - started) / 1000))
      countdown = Math.max(0, remaining - elapsed)
    }

    // Checkpoint-attached intel discovery (Feature: intel-on-checkpoint).
    // Fired after the capture state machine resolves, regardless of whether
    // the scan was a fresh capture, a reinforcement, or a contest — first
    // scan from a faction that hasn't yet seen it gets the intel screen.
    const cpIntel = await checkEntityIntel('checkpoint', cpId, storedEventId)
    if (cpIntel) {
      setResult({
        success: true,
        intel: true,
        message: '◆ INTEL DISCOVERED AT CHECKPOINT',
        intelMessage: cpIntel.message || row?.scanned_message || null,
        postScanInstruction: cpIntel.post_scan_instruction || 'LEAVE IN PLACE',
        points: cpIntel.player_intel_points || 0,
      })
      return
    }

    // ANU-168 — fail condition gate for checkpoint scans.
    const cpFailCheck = await checkFragoFailConditions('checkpoint', cpId, storedEventId, user.id)
    if (cpFailCheck.failed) {
      setResult({
        success: true,
        checkpoint: true,
        message: '⚠ FRAGO FAILED',
        intelMessage: `${cpFailCheck.missionName || 'FRAGO'} — ${cpFailCheck.objectiveTitle}`,
        postScanInstruction: 'Fail condition triggered. FRAGO cascade fired.',
      })
      return
    }

    // Phase 4 — fire the new FRAGO engine for checkpoint_scan objectives
    // and queued FRAGOs that activate on this checkpoint's capture.
    // Decorates the existing checkpoint result rather than replacing it,
    // because the capture state (countdown / hold) is still the lead
    // information the player needs.
    const cpEngine = await checkFragoObjectives('checkpoint', cpId, storedEventId, user.id)
    const { activated: cpActivated } = await activateFragosByEntityScan('checkpoint_scan', cpId, storedEventId, user.id)
    // Timer engine — escort_player objectives complete when the
    // destination checkpoint is scanned (with the target alive). Runs
    // alongside the regular checkpoint_scan objective check.
    const cpEscort = await checkEscortObjectives(cpId, storedEventId, user.id)
    const fragoExtras = []
    if (cpEngine.completed) fragoExtras.push(`◆ ${cpEngine.matchedTitle}`)
    if (cpEscort.completed) fragoExtras.push(`◆ ${cpEscort.matchedTitle} (escort delivered)`)
    if (cpEngine.phaseAdvanced) fragoExtras.push('Phase advanced.')
    if (cpEngine.fragoCompleted) fragoExtras.push('MISSION SUCCESS.')
    if (cpActivated.length > 0) fragoExtras.push(`FRAGO triggered: ${cpActivated[0].mission_name}`)

    // Bug 63 — checkpoint scan that activates a FRAGO lifts the
    // activation_message into the headline so the scanning player sees
    // the discovery moment.
    const triggeredFrago = cpActivated[0]
    setResult({
      success: true,
      checkpoint: true,
      message: triggeredFrago ? `▲ INTEL DISCOVERED — ${triggeredFrago.mission_name || 'FRAGO'}` : (row?.scanned_message || 'Checkpoint scanned'),
      intelMessage: triggeredFrago?.activation_message || (status ? `Status: ${status.toUpperCase()}` : null),
      postScanInstruction: [
        countdown != null
          ? `Hold position — ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2,'0')} remaining`
          : null,
        fragoExtras.join(' ') || null,
      ].filter(Boolean).join(' · ') || null,
    })
  }

  // Equipment QR handler. Looks up the equipment row, finds an active FRAGO
  // the scanner is on whose objective targets this equipment, marks the
  // objective complete, and auto-resolves the FRAGO if all objectives have
  // landed. If no objective matches: surface a soft success message and a
  // mission_events log entry so the GM still sees the scan.
  const handleEquipmentScan = async (equipId) => {
    if (!equipId || equipId.length < 8) {
      setResult({ success: false, message: 'Invalid equipment QR' })
      return
    }
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) {
      setResult({ success: false, message: 'Join an event first. Scan an ANUBIS-JOIN code or pick one in EVENTS.' })
      return
    }

    const { data: eqRows } = await supabase.from('event_equipment')
      .select('*').eq('id', equipId).limit(1)
    const equipment = eqRows?.[0]
    if (!equipment) {
      setResult({ success: false, message: 'Equipment not found.' })
      return
    }
    if (equipment.event_id !== storedEventId) {
      setResult({ success: false, message: 'This equipment belongs to a different event.' })
      return
    }

    // ANU-168 — fail condition gate for equipment scans.
    const equipFailCheck = await checkFragoFailConditions('equipment', equipId, storedEventId, user.id)
    if (equipFailCheck.failed) {
      setResult({
        success: true,
        equipment: true,
        message: '⚠ FRAGO FAILED',
        intelMessage: `${equipFailCheck.missionName || 'FRAGO'} — ${equipFailCheck.objectiveTitle}`,
        postScanInstruction: 'Fail condition triggered. FRAGO cascade fired.',
      })
      return
    }

    // Phase 4 of the FRAGO Rework — new auto-completion engine. Runs
    // before the legacy bounty/objective_order flow so structured FRAGOs
    // take precedence. Returns early if it lands a hit so we don't double-
    // render result panels. Same scan may also activate a queued FRAGO via
    // equipment_scan trigger — those run in parallel.
    const engineResult = await checkFragoObjectives('equipment', equipId, storedEventId, user.id)
    const { activated: equipActivated } = await activateFragosByEntityScan('equipment_scan', equipId, storedEventId, user.id)
    if (engineResult.completed || equipActivated.length > 0) {
      const parts = []
      if (engineResult.completed) parts.push(`◆ ${engineResult.matchedTitle}`)
      if (engineResult.phaseAdvanced) parts.push('Phase advanced.')
      if (engineResult.fragoCompleted) parts.push('MISSION SUCCESS.')
      if (equipActivated.length > 0) parts.push(`FRAGO triggered: ${equipActivated[0].mission_name}`)
      // Bug 63 — when a FRAGO activates from this scan, lift its
      // activation_message into the headline so the scanning player
      // sees the "you found intel" moment instead of just the
      // equipment name. Falls back to the existing label.
      const triggeredFrago = equipActivated[0]
      const fragoHeadline = triggeredFrago ? `▲ INTEL DISCOVERED — ${triggeredFrago.mission_name || 'FRAGO'}` : '◆ OBJECTIVE COMPLETE'
      setResult({
        success: true,
        equipment: true,
        message: fragoHeadline,
        intelMessage: triggeredFrago?.activation_message || equipment.name,
        postScanInstruction: parts.join(' '),
      })
      return
    }

    // Player's MCS membership in this event — used to scope active FRAGOs.
    const { data: memRows } = await supabase.from('mcs_node_members')
      .select('node_id, mcs_nodes!inner(event_id)').eq('user_id', user.id)
    const myNodeIds = (memRows || [])
      .filter(r => r.mcs_nodes?.event_id === storedEventId)
      .map(r => r.node_id)

    // FRAGO Rework: the legacy bounty_id-scoped frago_objectives table has
    // been renamed to frago_objectives_legacy_v1; the new public.frago_objectives
    // is phase-scoped (replaced by Phase 4's auto-completion engine).
    // Pointing this legacy equipment-scan flow at the renamed table so it
    // keeps working through Phase 3 — Phase 4 rewrites the entire path.
    const { data: candidateObjectives } = await supabase.from('frago_objectives_legacy_v1')
      .select(`
        id, bounty_id, description, status, objective_order,
        bounty:bounties!inner(id, mission_name, status, event_id, target_type, target_faction_id),
        bounty_assignments:bounties!inner(frago_assignments(node_id))
      `)
      .eq('trigger_equip_id', equipId)
      .eq('status', 'pending')

    // Filter in JS: bounty must be in this event, status='active', and the
    // scanner must either be on a node assigned to it OR (broadcast) be in
    // the targeted faction.
    let { data: epRows } = await supabase.from('event_players')
      .select('faction_id').eq('user_id', user.id).eq('event_id', storedEventId).limit(1)
    const myFactionId = epRows?.[0]?.faction_id || null

    let matched = null
    for (const obj of (candidateObjectives || [])) {
      const b = obj.bounty
      if (!b || b.event_id !== storedEventId || b.status !== 'active') continue
      // Resolve assignments via a dedicated query (the inline embed above
      // is unreliable across PostgREST joins).
      const { data: assigns } = await supabase.from('frago_assignments')
        .select('node_id').eq('bounty_id', b.id).in('status', ['active','accepted'])
      const assignedNodeIds = (assigns || []).map(a => a.node_id)
      const nodeMatch = assignedNodeIds.some(n => myNodeIds.includes(n))
      const factionMatch = b.target_type === 'faction' && b.target_faction_id === myFactionId
      if (nodeMatch || factionMatch) { matched = obj; break }
    }

    if (!matched) {
      // Log the scan so the GM has a trail; show soft success.
      // mission_events.bounty_id is NOT NULL, so this insert always fails
      // for orphan equipment scans — that's expected; we swallow the
      // constraint error and continue with the soft-success result.
      try {
        await supabase.from('mission_events').insert({
          bounty_id: null,
          event_type: 'equipment_scanned',
          triggered_by: user.id,
          notes: `Scanned ${equipment.name} — no active mission objective matches`,
        })
      } catch { /* non-blocking */ }

      // Equipment-attached intel discovery (Feature: intel-on-equipment).
      // Even with no FRAGO objective match, the scanner gets the intel
      // screen if a trigger was planted on this equipment.
      const equipIntel = await checkEntityIntel('equipment', equipment.id, storedEventId)
      if (equipIntel) {
        setResult({
          success: true,
          intel: true,
          message: '◆ INTEL DISCOVERED IN CACHE',
          intelMessage: equipIntel.message || equipment.name,
          postScanInstruction: equipIntel.post_scan_instruction || 'LEAVE IN PLACE',
          points: equipIntel.player_intel_points || 0,
        })
        return
      }

      setResult({
        success: true,
        equipment: true,
        message: 'EQUIPMENT SCANNED',
        intelMessage: equipment.name,
        postScanInstruction: 'No active mission objective for this item.',
      })
      return
    }

    // Flip the matched objective to completed (legacy table — see note above).
    await supabase.from('frago_objectives_legacy_v1').update({
      status: 'completed',
      completed_by: user.id,
      completed_at: new Date().toISOString(),
    }).eq('id', matched.id)

    // Check if every objective on the FRAGO is now done — auto-resolve.
    const { data: siblingObjectives } = await supabase.from('frago_objectives_legacy_v1')
      .select('id, status').eq('bounty_id', matched.bounty_id)
    const allDone = (siblingObjectives || []).every(o => o.status === 'completed')

    if (allDone) {
      await supabase.from('bounties').update({
        status: 'claimed', phase: 'resolved', claimed_at: new Date().toISOString(),
      }).eq('id', matched.bounty_id)
      await supabase.from('frago_assignments').update({ status: 'completed' })
        .eq('bounty_id', matched.bounty_id).in('status', ['active','accepted'])
      try {
        await supabase.from('mission_events').insert({
          bounty_id: matched.bounty_id,
          event_type: 'frago_completed',
          triggered_by: user.id,
          notes: `Auto-completed via equipment scan: ${equipment.name}`,
        })
      } catch { /* non-blocking */ }
    }

    try {
      await supabase.from('mission_events').insert({
        bounty_id: matched.bounty_id,
        event_type: 'equipment_scanned',
        triggered_by: user.id,
        notes: `${equipment.name} scanned — objective: ${matched.description}`,
      })
    } catch { /* non-blocking */ }

    // Equipment-attached intel discovery overlays the objective-complete
    // screen with the intel payload when a trigger is planted on this
    // equipment item. Both paths still log their own trails above; only
    // the player-facing modal is replaced.
    const equipIntel = await checkEntityIntel('equipment', equipment.id, storedEventId)
    if (equipIntel) {
      setResult({
        success: true,
        intel: true,
        message: '◆ INTEL DISCOVERED IN CACHE',
        intelMessage: equipIntel.message || matched.description,
        postScanInstruction: equipIntel.post_scan_instruction || 'LEAVE IN PLACE',
        points: equipIntel.player_intel_points || 0,
      })
      return
    }

    setResult({
      success: true,
      equipment: true,
      message: '◆ OBJECTIVE COMPLETE',
      intelMessage: matched.description,
      postScanInstruction: allDone ? 'MISSION SUCCESS — all objectives complete.' : 'Continue mission.',
    })
  }

  // After a confirmed kill is logged, check for any hidden intel triggers
  // attached to the target. If found, fire the same notification flow as
  // a field intel scan (linked bounty → awaiting_delegation, points,
  // mission_events 'intel_found') and surface a second result screen.
  // The original kill result is overwritten with the intel screen so the
  // scanner sees the more interesting follow-up.
  const checkTargetIntel = async (targetUserId, eventId) =>
    checkEntityIntel('player', targetUserId, eventId)

  // Polymorphic intel-trigger lookup. target_type / target_id were added in
  // migration 20260511100000_intel_target_polymorphic; before that the only
  // attached form was player (via attached_to_player_id). This helper queries
  // the new shape for 'player' / 'equipment' / 'checkpoint' triggers attached
  // to the entity being scanned. 'field' triggers are NOT matched here —
  // those have their own QR (ANUBIS-INTEL-{id}) and run through handleIntelScan.
  const checkEntityIntel = async (targetType, entityId, eventId) => {
    if (!targetType || !entityId || !eventId) return null
    if (targetType === 'field') return null

    const { data: trgRows } = await supabase.from('intel_triggers')
      .select('*')
      .eq('target_type', targetType)
      .eq('target_id', entityId)
      .eq('event_id', eventId)
      .eq('status', 'hidden')
      .limit(1)
    const trigger = trgRows?.[0]
    if (!trigger) return null

    const { data: epRows } = await supabase.from('event_players')
      .select('faction_id').eq('user_id', user.id).eq('event_id', eventId).limit(1)
    const factionId = epRows?.[0]?.faction_id || null
    if (!factionId) return null

    const { data: memRows } = await supabase.from('mcs_node_members')
      .select('node_id, mcs_nodes!inner(event_id)').eq('user_id', user.id)
    const nodeId = (memRows || []).find(r => r.mcs_nodes?.event_id === eventId)?.node_id || null

    // The resolve_intel_scan AFTER INSERT trigger does the rest atomically
    // (status flip, linked bounty advance, mission_events trail, points).
    // UNIQUE (trigger_id, faction_id) blocks a re-discovery by the same
    // faction → return null, caller falls back to the plain result.
    const { error: scanErr } = await supabase.from('intel_trigger_scans').insert({
      trigger_id: trigger.id, faction_id: factionId, scanned_by: user.id, node_id: nodeId,
    })
    if (scanErr) return null

    return trigger
  }

  const handleClaim = async (decodedText) => {
    const text = (decodedText || '').trim()

    // ── UNIVERSAL DISPATCH ──
    // routeQR returns { type, payload } for the prefixed Anubis QRs
    // (JOIN / INTEL / CP / EQUIP), the legacy deathcard://claim/{uuid} URL,
    // and the modern bare-UUID player patch. Each branch hands off to its
    // own handler, which owns its own event-state gating.
    const route = routeQR(text)
    if (route.type === 'frago')      return handleFragoTriggerScan(route.raw)
    if (route.type === 'intel')      return handleIntelScan(route.payload)
    if (route.type === 'equipment')  return handleEquipmentScan(route.payload)
    if (route.type === 'checkpoint') return handleCheckpointScan(route.payload)
    if (route.type === 'join') {
      window.location.href = `/join/${route.payload}`
      return
    }
    if (route.type !== 'kill') {
      setResult({ success: false, message: 'Unrecognized QR code' })
      return
    }

    // Kill scan — bare UUID or the legacy deathcard://claim/ URL both land
    // here with route.payload === target user id.
    const targetUserId = route.payload
    if (targetUserId === user.id) {
      setResult({ success: false, message: 'You cannot scan your own card' })
      return
    }

    // ── EVENT GATE — defense in depth ──
    // PlayerView already gates the scanner-open with auto-select / block, but
    // re-check here so kills NEVER record with a null event_id (per spec).
    const storedEventId = localStorage.getItem('dc_active_event')
    if (!storedEventId) {
      setResult({ success: false, message: 'Join an event first. Scan an ANUBIS-JOIN code or pick one in EVENTS.' })
      return
    }

    // ANU-168 — fail condition gate. If the scanned player is the target
    // of an active fail-condition objective and the scanner's faction
    // qualifies, the parent FRAGO auto-fails. Short-circuits the normal
    // objective-completion and kill paths.
    if (navigator.onLine) {
      const failCheck = await checkFragoFailConditions('player', targetUserId, storedEventId, user.id)
      if (failCheck.failed) {
        setResult({
          success: true,
          equipment: true,
          message: '⚠ FRAGO FAILED',
          intelMessage: `${failCheck.missionName || 'FRAGO'} — ${failCheck.objectiveTitle}`,
          postScanInstruction: 'Mission asset eliminated. FRAGO cascade fired.',
        })
        return
      }
    }

    // Amendment #1 priority gate — if the scanned player matches an
    // active player_scan objective for this scanner, this is a CONTACT
    // (pickup / link-up / find-the-VIP), NOT a kill. Mark the objective
    // complete and surface the contact panel instead of recording a kill.
    // "You're there to find them, not kill them."
    const playerScanMatch = await findPlayerScanObjective(targetUserId, user.id, storedEventId)
    if (playerScanMatch && navigator.onLine) {
      const contact = await checkFragoObjectives('player', targetUserId, storedEventId, user.id)
      const parts = [`◆ CONTACT CONFIRMED — ${contact.matchedTitle || playerScanMatch.title}`]
      if (contact.phaseAdvanced) parts.push('Phase advanced.')
      if (contact.fragoCompleted) parts.push('MISSION SUCCESS.')
      setResult({
        success: true,
        equipment: true, // re-use the readable-result panel that stays open until DONE
        message: '◆ CONTACT CONFIRMED',
        intelMessage: parts.join(' '),
        postScanInstruction: 'No kill recorded.',
      })
      return
    }

    const scannedAt = new Date().toISOString()

    // ANU-43 — pull the target's callsign / display_name so every kill
    // result panel can name who got tagged. The old "Kill recorded" /
    // "Bounty claimed" headlines didn't say. Lookup is best-effort:
    // RLS denial or a missing row falls back to the literal "TARGET"
    // so the result panel still renders cleanly. Offline path can't
    // fetch, so it stays generic.
    let targetName = 'TARGET'
    if (navigator.onLine) {
      try {
        const { data: targetProfile } = await supabase
          .from('users')
          .select('callsign, display_name')
          .eq('id', targetUserId)
          .limit(1)
        const t = targetProfile?.[0]
        if (t) targetName = (t.callsign || t.display_name || 'TARGET').toString().toUpperCase()
      } catch { /* leave default */ }
    }

    // ── OFFLINE PATH ──
    // Queue the kill claim through outbox. Bounty matching deferred
    // until connectivity returns. Player gets immediate feedback.
    if (!navigator.onLine) {
      try {
        const { queued } = await db.claimKill({
          killerId: user.id,
          targetId: targetUserId,
          eventId: storedEventId,
          bountyId: null,  // can't check bounties offline
          scanSource: 'qr_camera',
        })
        setResult({
          success: true,
          message: queued ? 'KILL QUEUED — will sync when back online' : 'KILL RECORDED',
          points: 0,
          queued: true,
        })
      } catch (err) {
        setResult({ success: false, message: 'Failed to queue kill claim' })
      }
      return
    }

    // ── ONLINE PATH ──
    try {
      // Step 1: Verify killer is registered for the active event (storedEventId
      // was already pulled from localStorage above and gated as non-null).
      let activeEventId = null

      if (storedEventId) {
        // Verify killer is registered for this event
        const { data: killerReg } = await supabase
          .from('event_players')
          .select('event_id')
          .eq('user_id', user.id)
          .eq('event_id', storedEventId)
          .in('reg_status', ['approved', 'active'])
          .limit(1)
        if (killerReg?.[0]) activeEventId = storedEventId
      }

      // Step 2: If we have an active event, verify target is in the same event
      if (activeEventId) {
        const { data: targetReg } = await supabase
          .from('event_players')
          .select('event_id')
          .eq('user_id', targetUserId)
          .eq('event_id', activeEventId)
          .in('reg_status', ['approved', 'active'])
          .limit(1)

        if (!targetReg?.[0]) {
          setResult({
            success: false,
            message: 'Target is not registered in your active event.',
          })
          return
        }
      }


      // Step 2.5: Check for defend bounties in prep phase — guard check-in
      const { data: defendBounties, error: defendErr } = await supabase
        .from('bounties')
        .select('id, target_id, phase, event_id')
        .eq('target_id', targetUserId)
        .eq('event_id', activeEventId)
        .eq('bounty_type', 'defend')
        .eq('status', 'active')
        .eq('phase', 'prep')


      if (defendBounties?.length > 0) {
        const { data: scannerFaction } = await supabase
          .from('event_players')
          .select('faction_id')
          .eq('user_id', user.id)
          .eq('event_id', activeEventId)
          .limit(1)

        const { data: vipFaction } = await supabase
          .from('event_players')
          .select('faction_id')
          .eq('user_id', targetUserId)
          .eq('event_id', activeEventId)
          .limit(1)

        const sameFaction = scannerFaction?.[0]?.faction_id && 
          scannerFaction[0].faction_id === vipFaction?.[0]?.faction_id

        if (sameFaction) {
          let checkedIn = 0
          for (const defend of defendBounties) {
            const { data: existing } = await supabase
              .from('bounty_guards')
              .select('id')
              .eq('bounty_id', defend.id)
              .eq('user_id', user.id)
              .limit(1)

            if (!existing?.[0]) {
              const { error: guardErr } = await supabase.from('bounty_guards').insert({
                bounty_id: defend.id,
                user_id: user.id,
                role: 'guard',
                checked_in_at: new Date().toISOString(),
                status: 'checked_in',
              })
              if (!guardErr) {
                checkedIn++
                await supabase.from('mission_events').insert({
                  bounty_id: defend.id,
                  event_type: 'guard_checkin',
                  triggered_by: user.id,
                  notes: 'Guard checked in via QR scan during prep phase',
                })
              }
            }
          }

          setResult({
            success: true,
            message: checkedIn > 0 
              ? 'Guard check-in confirmed. You are on protection detail.'
              : 'Already checked in for protection detail.',
            points: 0,
          })
          return
        }
        // Opposing faction scanning VIP during prep falls through to kill flow
      }

      // Step 2.5b: ANU-103 — PROTECT bounty check
      // Same-faction scan = offer to join protection detail (opt-in, not auto)
      // Opposing-faction scan = kill flow proceeds, auto-fail handled after kill recorded
      {
        const { data: protectBounties } = await supabase
          .from('bounties')
          .select('id, protect_mode, min_protectors, vip_player_id')
          .eq('target_id', targetUserId)
          .eq('event_id', activeEventId)
          .eq('bounty_type', 'protect')
          .in('status', ['registration', 'active'])
          .limit(1)

        if (protectBounties?.length > 0) {
          const pb = protectBounties[0]
          const { data: scannerEp } = await supabase.from('event_players').select('faction_id').eq('user_id', user.id).eq('event_id', activeEventId).limit(1)
          const { data: vipEp } = await supabase.from('event_players').select('faction_id').eq('user_id', targetUserId).eq('event_id', activeEventId).limit(1)
          const sameFaction = scannerEp?.[0]?.faction_id && scannerEp[0].faction_id === vipEp?.[0]?.faction_id

          if (sameFaction) {
            const { data: existingReg } = await supabase.from('protect_registrations').select('id').eq('bounty_id', pb.id).eq('protector_id', user.id).limit(1)
            setResult({
              success: true,
              message: 'FRIENDLY SCANNED',
              intelMessage: existingReg?.[0]
                ? 'You are already on this protection detail.'
                : 'This player has an active PROTECT bounty. Join their protection detail?',
              intel: !existingReg?.[0],
              protectBountyId: existingReg?.[0] ? null : pb.id,
              alreadyOnProtect: !!existingReg?.[0],
              points: 0,
            })
            return
          }
          // Opposing faction: fall through to kill — failProtectBounties runs after kill recorded
        }
      }

      // Step 2.6: Detect friendly fire (event-scoped + faction-scoped, then team-scoped)
      // Returns null | 'team_kill' | 'faction_kill'. Friendly fire = zero points.
      // Pass storedEventId (the user's selected event) rather than activeEventId
      // (which only resolves after the killer-registration check in Step 1) — the
      // helper makes its own queries against event_players and handles "not in event"
      // by returning null. This way, friendly-fire detection works whenever the user
      // has an event selected, not only when their reg_status is approved/active.
      const killType = await detectFriendlyFire(user.id, targetUserId, storedEventId)
      const isFriendly = killType === 'team_kill' || killType === 'faction_kill'

      // Step 3: Check for active kill bounties on target in this event
      let bountyQuery = supabase
        .from('bounties')
        .select('*')
        .eq('target_id', targetUserId)
        .eq('status', 'active')
        .eq('bounty_type', 'kill')
        .order('points', { ascending: false })

      if (activeEventId) bountyQuery = bountyQuery.eq('event_id', activeEventId)

      const { data: bountyData, error: bountyErr } = await bountyQuery
      if (bountyErr) throw bountyErr

      const activeBounties = bountyData ?? []

      if (activeBounties.length === 0) {
        // No bounty — record kill with event association. Use storedEventId
        // (always non-null, gated above) so kill_log.event_id is never null
        // even if the killer-reg check in Step 1 left activeEventId null.
        const { queued } = await db.claimKill({
          killerId: user.id,
          targetId: targetUserId,
          eventId: storedEventId,
          bountyId: null,
          scanSource: 'qr_camera',
          killType,
          pointsAwarded: 0,
        })

        await failDefendBounties(targetUserId, user.id)
        await failProtectBounties(targetUserId, user.id)

        // Phase 4 — FRAGO engine kill hook: completes any kill_target
        // objectives, fails defend_player objectives targeting the killed
        // player, and activates kill_event triggers. Non-blocking; the
        // kill is already recorded by this point.
        const killEngine = await onKillRecorded({ killerId: user.id, targetId: targetUserId, eventId: storedEventId })

        // ANU-40 — also evaluate player_scan ACTIVATION triggers. The
        // kill flow used to only fire kill_event triggers via
        // onKillRecorded; player_scan triggered FRAGOs (e.g., "activate
        // the rescue mission when anyone scans VIP X") got dropped on
        // the floor because the equipment/checkpoint branches were the
        // only callers of activateFragosByEntityScan.
        const playerScanFrago = await activateFragosByEntityScan('player_scan', targetUserId, storedEventId, user.id)

        // Target-intel hook: hidden intel triggers attached to this target
        // fire on a successful kill. If found, surface the intel screen
        // instead of the plain kill confirmation.
        const targetIntel = await checkTargetIntel(targetUserId, storedEventId)
        if (targetIntel) {
          setResult({
            success: true,
            intel: true,
            message: '◆ INTEL DISCOVERED ON TARGET',
            intelMessage: targetIntel.message || null,
            postScanInstruction: targetIntel.post_scan_instruction || 'LEAVE IN PLACE',
            points: targetIntel.player_intel_points || 0,
          })
          return
        }

        // Bug 63 — kill_event-triggered FRAGO: surface the activation
        // message to the killer so they see the "you triggered something"
        // moment instead of the bare no-bounty kill confirmation.
        // ANU-40 — same panel surfaces player_scan-triggered FRAGOs.
        const killTriggeredFrago = killEngine?.activated?.[0] || playerScanFrago?.activated?.[0]
        if (killTriggeredFrago) {
          setResult({
            success: true,
            intel: true,
            message: `▲ INTEL DISCOVERED — ${killTriggeredFrago.mission_name || 'FRAGO'}`,
            intelMessage: killTriggeredFrago.activation_message || 'New mission unlocked.',
            postScanInstruction: 'Check MISSIONS tab for orders.',
            points: 0,
            queued,
          })
          return
        }

        setResult({
          success: true,
          message: queued
            ? `KILL QUEUED — ${targetName} (syncing...)`
            : isFriendly
              ? `KILL RECORDED — ${targetName} (${killType.replace('_', ' ')} — no points)`
              : `KILL RECORDED — ${targetName} (no active bounty)`,
          points: 0,
          queued,
        })
        return
      }

      // Step 4: Claim ALL active kill bounties on target
      let totalPoints = 0
      let anyQueued = false

      for (const bounty of activeBounties) {
        const pointsAwarded = isFriendly ? 0 : (bounty.points || 0)
        const { queued } = await db.claimKill({
          killerId: user.id,
          targetId: targetUserId,
          eventId: bounty.event_id,
          bountyId: bounty.id,
          scanSource: 'qr_camera',
          killType,
          pointsAwarded,
        })
        totalPoints += pointsAwarded

        // If this kill bounty is linked to a defend, fail the defend
        if (bounty.linked_bounty_id) {
          await supabase.from('bounties')
            .update({ status: 'failed', phase: 'failed' })
            .eq('id', bounty.linked_bounty_id)

          await supabase.from('mission_events').insert({
            bounty_id: bounty.linked_bounty_id,
            event_type: 'vip_down',
            triggered_by: user.id,
            notes: 'Linked kill bounty claimed — defend bounty failed',
          })

          await supabase.from('bounty_guards')
            .update({ status: 'unverified' })
            .eq('bounty_id', bounty.linked_bounty_id)
            .in('status', ['checked_in', 'active'])
        }

        if (queued) anyQueued = true
      }

    await failDefendBounties(targetUserId, user.id)

      // Phase 4 — FRAGO engine kill hook (bounty path mirror of the
      // no-bounty branch above).
      const killEngine = await onKillRecorded({ killerId: user.id, targetId: targetUserId, eventId: storedEventId })
      // ANU-40 — also evaluate player_scan ACTIVATION triggers (mirror
      // of the no-bounty branch).
      const playerScanFrago = await activateFragosByEntityScan('player_scan', targetUserId, storedEventId, user.id)

      // Target-intel hook (bounty path) — same as no-bounty path. The intel
      // screen takes precedence over the bounty-claimed confirmation when
      // the target was carrying hidden intel.
      const targetIntel = await checkTargetIntel(targetUserId, storedEventId)
      if (targetIntel) {
        setResult({
          success: true,
          intel: true,
          message: '◆ INTEL DISCOVERED ON TARGET',
          intelMessage: targetIntel.message || null,
          postScanInstruction: targetIntel.post_scan_instruction || 'LEAVE IN PLACE',
          points: (totalPoints || 0) + (targetIntel.player_intel_points || 0),
        })
        return
      }

      const bountyWord = activeBounties.length === 1 ? 'Bounty' : `${activeBounties.length} Bounties`
      const friendlyTag = isFriendly ? ` (${killType.replace('_', ' ')} — no points)` : ''
      // Bug 63 — surface FRAGO activation_message as a sibling line on
      // the bounty success result so it's not lost behind the headline.
      // ANU-40 — same panel surfaces player_scan-triggered FRAGOs.
      const killTriggeredFrago = killEngine?.activated?.[0] || playerScanFrago?.activated?.[0]
      setResult({
        success: true,
        message: anyQueued
          ? `${bountyWord.toUpperCase()} CLAIMED — ${targetName} (syncing...)${friendlyTag}`
          : `${bountyWord.toUpperCase()} CLAIMED — ${targetName}${friendlyTag}`,
        intelMessage: killTriggeredFrago ? `▲ NEW FRAGO: ${killTriggeredFrago.mission_name || 'mission unlocked'}` : null,
        postScanInstruction: killTriggeredFrago?.activation_message || null,
        points: totalPoints,
        queued: anyQueued,
      })
    } catch (err) {
      // Online path failed — try to queue through outbox as fallback
      const msg = err?.message || ''
      const isNetwork = !navigator.onLine ||
        msg.includes('Failed to fetch') ||
        msg.includes('NetworkError') ||
        msg.includes('ERR_CONNECTION')

      if (isNetwork) {
        // Lost connectivity mid-claim — queue it. storedEventId is in scope
        // from the top of handleClaim and was already gated as non-null.
        try {
          await db.claimKill({
            killerId: user.id,
            targetId: targetUserId,
            eventId: storedEventId,
            bountyId: null,
            scanSource: 'qr_camera',
          })
          setResult({
            success: true,
            message: 'Connection lost — kill queued for sync',
            points: 0,
            queued: true,
          })
        } catch (queueErr) {
          setResult({ success: false, message: 'Failed to record kill. Please try again.' })
        }

      } else {
        setResult({
          success: false,
          message: 'Server error — kill not recorded. Please try again.',
        })
      }
    }
  }

  // ── RECON-THEMED UI ──
  const S = {
    display: { fontFamily: "'Rajdhani', 'Saira Condensed', sans-serif", letterSpacing: '0.14em', textTransform: 'uppercase' },
    btnPrimary: { width: '100%', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: '#4dd6c2', color: '#14181a', border: 'none', padding: '14px 20px', cursor: 'pointer', boxShadow: '0 0 8px rgba(77,214,194,.3)', fontSize: '16px' },
    btnCancel: { width: '100%', fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', background: 'rgba(28, 38, 38, .6)', color: '#e0e8de', border: '1px solid rgba(140, 180, 130, .2)', padding: '14px 20px', cursor: 'pointer', fontSize: '16px', backdropFilter: 'blur(6px)' },
    resultBox: (success) => ({
      padding: '20px',
      textAlign: 'center',
      background: success ? 'rgba(77, 214, 194, .08)' : 'rgba(255, 106, 61, .08)',
      border: `1px solid ${success ? 'rgba(77, 214, 194, .3)' : 'rgba(255, 106, 61, .3)'}`,
    }),
  }

  return (
    <div className="space-y-4">
      <video ref={videoRef} className={scanning ? 'w-full' : 'hidden'} playsInline muted style={scanning ? { border: '1px solid rgba(140,180,130,.2)' } : {}} />
      <canvas ref={canvasRef} className="hidden" />

      {!scanning && !result && (
        <button onClick={startScanner} style={S.btnPrimary}>OPEN CAMERA</button>
      )}

      {scanning && (
        <button onClick={stopScanner} style={S.btnCancel}>CANCEL</button>
      )}

      {error && (
        <div style={{ background: 'rgba(255,106,61,.08)', border: '1px solid rgba(255,106,61,.3)', padding: '16px', color: '#ff6a3d', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {result && (() => {
        // Intel + equipment scans share the amber-themed result panel; the
        // result-screen text differs but the chrome is the same.
        const isAccent = result.intel || result.equipment
        return (
        <div style={isAccent
          ? { padding: 20, textAlign: 'center', background: 'rgba(255, 204, 51, .08)', border: '1px solid rgba(255, 204, 51, .4)' }
          : S.resultBox(result.success)
        }>
          <p style={{ fontWeight: 700, fontSize: isAccent ? '20px' : '18px', color: isAccent ? '#ffcc33' : (result.success ? '#4dd6c2' : '#ff6a3d'), ...S.display }}>
            {result.message}
          </p>
          {result.intelMessage && (
            <p style={{ color: '#e0e8de', fontSize: 14, marginTop: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {result.intelMessage}
            </p>
          )}
          {isAccent && result.postScanInstruction && (
            <p style={{ color: '#ff6a3d', fontWeight: 700, fontSize: 14, marginTop: 12, ...S.display }}>
              {result.postScanInstruction}
            </p>
          )}
          {result.intel && (
            <p style={{ color: '#8aa090', fontSize: 11, marginTop: 12, fontStyle: 'italic' }}>
              Command has been notified.
            </p>
          )}
          {result.points > 0 && (
            <p style={{ color: '#9adb6e', fontWeight: 700, fontSize: '28px', marginTop: '8px', ...S.display }}>
              +{result.points} PTS
            </p>
          )}
          {result.queued && (
            <p style={{ color: '#8aa090', fontSize: '11px', marginTop: '8px' }}>
              Will sync automatically when online
            </p>
          )}
          {/* ANU-103 — JOIN PROTECTION DETAIL button */}
          {result.protectBountyId && (
            <button
              disabled={joinProtectBusy}
              onClick={async () => {
                setJoinProtectBusy(true)
                const { error: regErr } = await supabase.from('protect_registrations').insert({ bounty_id: result.protectBountyId, protector_id: user.id })
                if (!regErr) {
                  setResult(r => ({ ...r, protectBountyId: null, intelMessage: 'You are now on protection detail.', intel: false }))
                } else {
                  setResult(r => ({ ...r, protectBountyId: null, intelMessage: regErr.message || 'Failed to join detail.', intel: false }))
                }
                setJoinProtectBusy(false)
              }}
              style={{ width: '100%', padding: '12px', marginTop: 12, fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'rgba(77,160,255,.15)', color: '#4da0ff', border: '1px solid rgba(77,160,255,.4)', cursor: 'pointer' }}>
              {joinProtectBusy ? 'JOINING...' : '🛡 JOIN PROTECTION DETAIL'}
            </button>
          )}
          {/* ANU-44 — split the dismiss affordance into two prominent
              actions. The 60s auto-timeout still fires for non-readable
              results so an unattended scanner clears itself, but the
              attended case now has a clear "go back" + "scan another"
              path instead of a single 12px text link. */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => { if (onResult) onResult() }} style={{
              width: '100%', padding: '12px 16px',
              background: '#4dd6c2', color: '#0e1116', border: 'none',
              fontFamily: "'Orbitron', 'Rajdhani', sans-serif",
              fontSize: 14, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', cursor: 'pointer',
              boxShadow: '0 0 10px rgba(77,214,194,.35)',
            }}>
              ← RETURN TO PLAYER VIEW
            </button>
            <button onClick={() => { setResult(null); setError(''); startScanner() }} style={{
              width: '100%', padding: '10px 16px',
              background: 'rgba(154,219,110,.08)', color: '#9adb6e',
              border: '1px solid rgba(154,219,110,.4)',
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 13, fontWeight: 600, letterSpacing: '0.12em',
              textTransform: 'uppercase', cursor: 'pointer',
            }}>
              SCAN ANOTHER
            </button>
          </div>
          {result.success && !isAccent && (
            <p style={{ color: '#5a6a60', fontSize: '10px', marginTop: '8px' }}>
              Auto-returning in ~60s if no action taken.
            </p>
          )}
        </div>
        )
      })()}
    </div>
  )
}
